/**
 * Org-wide free-trial status banner — mounted in AppShell (same spot as
 * DpaBanner) so it appears across my/Frame/Ping alike. This is what "my
 * follows whatever other app has subscription" means for the shell: my has
 * no gate of its own (see SubscriptionPage.tsx's AppRow for the per-app
 * detail), it just surfaces the org's Subscription state honestly wherever
 * the user happens to be — informational only, never blocks anything itself
 * (Frame's own hard lock, when its trial screen license lapses, comes from
 * RequireOrgEntitlement separately — see OrgAccessDeniedPage).
 *
 * Renders nothing unless status is "trialing" within WARN_WINDOW_DAYS of
 * trial_ends_at, or "expired". Silently no-ops (like DpaBanner) if the
 * signed-in user lacks BILLING_VIEW — informational banner, not worth a
 * broken page over.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getSubscription, type Subscription } from '../lib/api'

const WARN_WINDOW_DAYS = 7
const MY_URL = ((import.meta as any).env?.VITE_MY_URL as string || '').replace(/\/$/, '')

export default function TrialStatusBanner() {
  const { t } = useTranslation()
  const [sub, setSub] = useState<Subscription | null>(null)

  useEffect(() => {
    let alive = true
    getSubscription()
      .then(({ data }) => { if (alive) setSub(data) })
      .catch(() => { /* non-blocking — BILLING_VIEW-less users just see nothing */ })
    return () => { alive = false }
  }, [])

  if (!sub) return null

  const isExpired = sub.status === 'expired'
  const daysLeft = sub.trial_ends_at
    ? Math.ceil((new Date(sub.trial_ends_at).getTime() - Date.now()) / 86_400_000)
    : null
  const isEndingSoon = sub.status === 'trialing' && daysLeft !== null && daysLeft <= WARN_WINDOW_DAYS

  if (!isExpired && !isEndingSoon) return null

  const tone = isExpired
    ? 'border-red-300 bg-red-50 text-red-900'
    : 'border-amber-300 bg-amber-50 text-amber-900'
  const buttonTone = isExpired
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-amber-600 hover:bg-amber-700'

  return (
    <div className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${tone}`}>
      <span>
        {isExpired
          ? t('trialBanner.expired', 'Your free trial has ended. Upgrade to keep using every app on your plan.')
          : t('trialBanner.endingSoon', { count: Math.max(1, daysLeft ?? 0) })}
      </span>
      {MY_URL && (
        <a
          href={`${MY_URL}/subscription`}
          className={`shrink-0 rounded-lg px-3 py-1.5 font-semibold text-white ${buttonTone}`}
        >
          {t('trialBanner.upgrade', 'Upgrade')}
        </a>
      )}
    </div>
  )
}
